import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import AddTalentPopup from '../TalentComponents/AddTalentPopup';
import TalentStatus from '../TalentComponents/Utils/TalentStatus';
import EvalStatus from '../EvaluationComponents/Utils/EvalStatus';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getColorForValue } from '../Utils/ColorUtils';

// Helper function to extract top5 skills from hard_skills object
const getTop5Skills = (hardSkills) => {
  if (!hardSkills || !hardSkills.items || !Array.isArray(hardSkills.items)) {
    return [];
  }
  return hardSkills.items
    .filter(item => item.top5 === true)
    .map(item => item.skill);
};

// Circular progress chart component for ability score
const CircularProgressChart = ({ value, maxValue = 10 }) => {
  // Parse value to ensure it's a number
  const numericValue = parseFloat(value) || 0;
  
  // Calculate the percentage
  const percentage = (numericValue / maxValue) * 100;
  
  // Create data for the pie chart
  const data = [
    { name: 'Value', value: percentage },
    { name: 'Remaining', value: 100 - percentage }
  ];
  
  // Get color based on value
  const color = getColorForValue(numericValue);
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius="60%"
              outerRadius="80%"
              dataKey="value"
              cornerRadius={3}
            >
              <Cell fill={color} />
              <Cell fill="#f3f4f6" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-sm font-bold">{numericValue}</div>
        </div>
      </div>
    </div>
  );
};

const Talent = () => {
  const [talent, setTalent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddTalentPopupOpen, setIsAddTalentPopupOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  
  // Search state
  const [searchName, setSearchName] = useState('');
  const [evaluationStatus, setEvaluationStatus] = useState('');
  const [talentStatus, setTalentStatus] = useState('');
  
  // Set document title
  useEffect(() => {
    document.title = "Talent";
  }, []);
  
  // Pagination state
  const [pageSize] = useState(25); // Number of items per page
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useSession();

  // Function to update URL with current search and pagination parameters
  const updateURL = (searchParams, offset, page, replace = false) => {
    const urlParams = new URLSearchParams();
    
    if (searchParams.searchName && searchParams.searchName.trim()) {
      urlParams.set('search_name', searchParams.searchName.trim());
    }
    if (searchParams.evaluationStatus && searchParams.evaluationStatus.trim()) {
      urlParams.set('evaluation_status', searchParams.evaluationStatus.trim());
    }
    if (searchParams.talentStatus && searchParams.talentStatus.trim()) {
      urlParams.set('talent_status', searchParams.talentStatus.trim());
    }
    if (offset > 0) {
      urlParams.set('o', offset.toString());
    }
    if (page > 1) {
      urlParams.set('p', page.toString());
    }
    
    const newUrl = urlParams.toString() ? `${location.pathname}?${urlParams.toString()}` : location.pathname;
    navigate(newUrl, { replace });
  };

  // Function to read URL parameters and set state
  const readURLParams = () => {
    const urlParams = new URLSearchParams(location.search);
    
    const searchName = urlParams.get('search_name') || '';
    const evaluationStatus = urlParams.get('evaluation_status') || '';
    const talentStatus = urlParams.get('talent_status') || '';
    const offset = parseInt(urlParams.get('o') || '0', 10);
    const page = parseInt(urlParams.get('p') || '1', 10);
    
    setSearchName(searchName);
    setEvaluationStatus(evaluationStatus);
    setTalentStatus(talentStatus);
    setCurrentOffset(offset);
    setCurrentPage(page);
    
    return { searchName, evaluationStatus, talentStatus, offset, page };
  };

  // Function to fetch talent from the API
  const fetchTalent = async (offset = 0, searchParams = {}) => {
    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the talent endpoint with pagination parameters as URL query parameters
      const url = new URL(API_ENDPOINTS.GET_TALENT);
      url.searchParams.append('limit', pageSize);
      url.searchParams.append('offset', offset);
      
      // Add search parameters if provided
      if (searchParams.searchName && searchParams.searchName.trim()) {
        url.searchParams.append('search_name', searchParams.searchName.trim());
      }
      if (searchParams.evaluationStatus && searchParams.evaluationStatus.trim()) {
        url.searchParams.append('evaluation_status', searchParams.evaluationStatus.trim());
      }
      if (searchParams.talentStatus && searchParams.talentStatus.trim()) {
        url.searchParams.append('talent_status', searchParams.talentStatus.trim());
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });

      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Server responded with status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log('Talent data:', data);
      } catch (parseError) {
        console.log('Empty or invalid response received');
        data = [];
      }
      
      // Update the talent state with the fetched data
      // Check if this is the last page (if we received fewer items than the page size)
      setIsLastPage(data.length < pageSize);
      
      setTalent(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching talent:', err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch talent');
    } finally {
      setLoading(false);
    }
  };

  // Initial load - read URL parameters and fetch data
  useEffect(() => {
    const urlParams = readURLParams();
    const searchParams = {
      searchName: urlParams.searchName,
      evaluationStatus: urlParams.evaluationStatus,
      talentStatus: urlParams.talentStatus
    };
    fetchTalent(urlParams.offset, searchParams);
  }, [location.search]);
  
  // Handle next page
  const handleNextPage = () => {
    if (!isLastPage) {
      const newOffset = currentOffset + pageSize;
      const newPage = currentPage + 1;
      const searchParams = {
        searchName,
        evaluationStatus,
        talentStatus
      };
      setCurrentOffset(newOffset);
      setCurrentPage(newPage);
      updateURL(searchParams, newOffset, newPage, false); // Create new history entry
      fetchTalent(newOffset, searchParams);
    }
  };
  
  // Handle previous page
  const handlePrevPage = () => {
    if (currentOffset > 0) {
      const newOffset = Math.max(0, currentOffset - pageSize);
      const newPage = currentPage - 1;
      const searchParams = {
        searchName,
        evaluationStatus,
        talentStatus
      };
      setCurrentOffset(newOffset);
      setCurrentPage(newPage);
      updateURL(searchParams, newOffset, newPage, false); // Create new history entry
      fetchTalent(newOffset, searchParams);
    }
  };


  // Format date to a compact format with numbers only
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleAddTalentClick = () => {
    setIsAddTalentPopupOpen(true);
  };

  const handleAddTalentClose = () => {
    setIsProcessing(false);
    setProcessingError(null);
    setIsAddTalentPopupOpen(false);
  };

  const handleAddTalentSubmit = async (responseData) => {
    // Check if there was an authentication error
    if (responseData && responseData.authError) {
      console.log('Authentication error detected. Redirecting to login page.');
      logout();
      navigate('/login');
      return;
    }
    
    console.log('Talent added successfully:', responseData);
    
    // Refresh the talent list - reset to first page when new talent is added
    setCurrentOffset(0);
    setCurrentPage(1);
    fetchTalent(0);
    
    // Reset processing state
    setIsProcessing(false);
    setProcessingError(null);
  };

  // Handle search submission
  const handleSearch = () => {
    const searchParams = {
      searchName,
      evaluationStatus,
      talentStatus
    };
    const newOffset = 0;
    const newPage = 1;
    setCurrentOffset(newOffset);
    setCurrentPage(newPage);
    updateURL(searchParams, newOffset, newPage, false); // Create new history entry
    fetchTalent(newOffset, searchParams);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]"> {/* Added top padding to account for fixed header */}
      <Navigation title="Talent" showBackButton={false} />
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="border-b border-gray-200 pb-3 mb-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-800">
              Available Talent
            </h2>
            
            {/* Search fields - positioned right next to title */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="min-w-[180px]">
                <input
                  type="text"
                  id="searchName"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Search by talent name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              
              <div className="min-w-[160px]">
                <select
                  id="evaluationStatus"
                  value={evaluationStatus}
                  onChange={(e) => setEvaluationStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Evaluation Statuses</option>
                  <option value="es_not_started">Evaluation: Not Started</option>
                  <option value="es_completed">Evaluation: Completed</option>
                </select>
              </div>
              
              <div className="min-w-[160px]">
                <select
                  id="talentStatus"
                  value={talentStatus}
                  onChange={(e) => setTalentStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Talent Statuses</option>
                  <option value="ts_completed">Talent: Completed</option>
                  <option value="ts_role_not_supported">Talent: Role Not Supported</option>
                </select>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-green-500 text-white font-semibold rounded hover:bg-green-600 transition-colors flex items-center text-sm"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                  Search
                </button>
                <button
                  onClick={() => {
                    setSearchName('');
                    setEvaluationStatus('');
                    setTalentStatus('');
                    const newOffset = 0;
                    const newPage = 1;
                    setCurrentOffset(newOffset);
                    setCurrentPage(newPage);
                    updateURL({}, newOffset, newPage, false); // Create new history entry
                    fetchTalent(newOffset, {});
                  }}
                  className="px-3 py-2 bg-gray-500 text-white font-semibold rounded hover:bg-gray-600 transition-colors flex items-center text-sm"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4l16 16m0-16L4 20"></path>
                  </svg>
                  Clear
                </button>
              </div>
            </div>
            
            {/* Add Talent Button - positioned at the end */}
            <div className="lg:ml-auto">
              <button 
                onClick={handleAddTalentClick}
                className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Talent
              </button>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading talent...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or refresh the page.</p>
          </div>
        ) : talent.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No talent available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Talent
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profile Assessment
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Evaluations
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {talent.map((person, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/talentdetails?tid=${person.talent_id}`)}
                  >
                    <td className="px-4 py-4 text-base font-medium text-gray-900" style={{ maxWidth: '400px' }}>
                      <div>
                        <p className="font-bold">
                          {person.fname && person.lname 
                            ? `${person.fname} ${person.lname}` 
                            : person.fname || person.lname || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">{person.role || 'No role specified'}</p>
                        {(() => {
                          const top5Skills = getTop5Skills(person.hard_skills);
                          return top5Skills.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {top5Skills.map((skill, skillIndex) => (
                                <span 
                                  key={skillIndex}
                                  className="px-2 py-1 text-xs font-regular rounded-full"
                                  style={{ 
                                    backgroundColor: 'var(--color-brand-blue-light)',
                                    color: 'var(--color-brand-blue)'
                                  }}
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {person.created_at ? formatDate(person.created_at) : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TalentStatus status={person.status} showDot={true} />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {!person.assessment_ability && !person.assessment_ai && !person.assessment_security ? (
                        <span className="text-sm text-gray-400">N/A</span>
                      ) : (
                        <div className="flex gap-4 items-center">
                          {/* Ability Chart */}
                          {person.assessment_ability ? (
                            <div className="flex flex-col items-center">
                              <CircularProgressChart value={person.assessment_ability} />
                              <span className="text-xs text-gray-500 mt-1">Ability</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 flex items-center justify-center">
                                <span className="text-xs text-gray-400">N/A</span>
                              </div>
                              <span className="text-xs text-gray-500 mt-1">Ability</span>
                            </div>
                          )}
                          
                          {/* AI Chart */}
                          {person.assessment_ai ? (
                            <div className="flex flex-col items-center">
                              <CircularProgressChart value={person.assessment_ai} />
                              <span className="text-xs text-gray-500 mt-1">AI</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 flex items-center justify-center">
                                <span className="text-xs text-gray-400">N/A</span>
                              </div>
                              <span className="text-xs text-gray-500 mt-1">AI</span>
                            </div>
                          )}
                          
                          {/* Security Chart */}
                          {person.assessment_security ? (
                            <div className="flex flex-col items-center">
                              <CircularProgressChart value={person.assessment_security} />
                              <span className="text-xs text-gray-500 mt-1">Security</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 flex items-center justify-center">
                                <span className="text-xs text-gray-400">N/A</span>
                              </div>
                              <span className="text-xs text-gray-500 mt-1">Security</span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-1">
                        {person.evals && person.evals.length > 0 ? (
                          person.evals.map((evaluation, evalIndex) => (
                            <div key={evalIndex} className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">{evaluation.evaluations?.name || 'Unnamed'}:</span>
                              <EvalStatus status={evaluation.status} showDot={true} size="sm" />
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No evaluations</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click event
                          
                          // Show confirmation dialog
                          if (window.confirm(`Are you sure you want to delete talent "${person.fname && person.lname ? `${person.fname} ${person.lname}` : person.fname || person.lname || 'N/A'}"?`)) {
                            // User confirmed, send delete request
                            const deleteTalent = async () => {
                              try {
                                // Create URL with talent ID parameter
                                const url = new URL(API_ENDPOINTS.DELETE_TALENT);
                                url.searchParams.append('tid', person.talent_id);
                                
                                const response = await fetch(url, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${user.access_token}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error(`Error: ${response.status} ${response.statusText}`);
                                }
                                
                                // Remove the deleted talent from the list
                                setTalent(prevTalent => 
                                  prevTalent.filter(item => item.talent_id !== person.talent_id)
                                );
                              } catch (err) {
                                console.error('Error deleting talent:', err);
                                alert('Failed to delete talent. Please try again.');
                              }
                            };
                            
                            deleteTalent();
                          }
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing page {currentPage} {isLastPage ? '(last page)' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentOffset === 0}
                  className={`px-4 py-2 border rounded-md ${
                    currentOffset === 0 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={isLastPage}
                  className={`px-4 py-2 border rounded-md ${
                    isLastPage 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Add Talent Popup */}
      <AddTalentPopup 
        isOpen={isAddTalentPopupOpen}
        onClose={handleAddTalentClose}
        onSubmit={handleAddTalentSubmit}
        isProcessing={isProcessing}
        processingError={processingError}
      />
    </div>
  );
};

export default Talent;
